import React, { useMemo, useState } from 'react';
import Link from './Link';
import { fmtDate, fmtMoney } from './format';
import { canTrack, countedOnly, filterInvoices, getTrackingUrl, sum } from './salesCalculations';
import { amountStyle, cardStyle, footRowStyle, headRowStyle, headStyle, headTextStyle, linkStyle, pageHeaderStyle, stripeStyle, tableStyle, textCellStyle } from './styles';

const compareText = (pick) => (a, b) => (pick(a) || '').localeCompare(pick(b) || '', 'sv');
const compareNumber = (pick) => (a, b) => (pick(a) || 0) - (pick(b) || 0);

const AMOUNT_COLUMNS = [
    { key: 'net', label: 'Netto', pick: (invoice) => invoice.net, compare: compareNumber((i) => i.net) },
    { key: 'freight', label: 'Frakt', pick: (invoice) => invoice.freight, compare: compareNumber((i) => i.freight) },
    { key: 'vat', label: 'Moms', pick: (invoice) => invoice.totalVAT },
    { key: 'total', label: 'Totalt inkl. moms', pick: (invoice) => invoice.total, compare: compareNumber((i) => i.total) },
];

const COMPARERS = {
    customer: compareText((i) => i.customerName),
    ourReference: compareText((i) => i.ourReference),
    sent: (a, b) => Number(!!a.sent) - Number(!!b.sent),
    ...Object.fromEntries(AMOUNT_COLUMNS.filter((c) => c.compare).map((c) => [c.key, c.compare])),
};

const filterInputStyle = {
    width: '100%',
    maxWidth: '24rem',
    height: '2.25rem',
    margin: '0.75rem 0 0',
    padding: '0 0.75rem',
    fontSize: '0.9rem',
    border: '1px solid #cacaca',
    borderRadius: '0.25rem',
};

const sortableStyle = { cursor: 'pointer', userSelect: 'none' };

const InvoiceList = ({ title, invoices, unplacedInvoices, onBack, onSelect, onOpenOrder }) => {
    const [filter, setFilter] = useState('');
    // null sortKey keeps the order of the list. Clicking a column cycles ascending, descending, original order.
    const [sort, setSort] = useState({ key: null, descending: false });

    const toggleSort = (key) => setSort((prev) => {
        if (prev.key !== key) return { key, descending: false };
        return prev.descending ? { key: null, descending: false } : { key, descending: true };
    });

    const visibleInvoices = useMemo(() => {
        const filtered = filterInvoices(invoices, filter);
        if (!sort.key) return filtered;

        const compare = COMPARERS[sort.key];
        return [...filtered].sort((a, b) => (sort.descending ? compare(b, a) : compare(a, b)));
    }, [invoices, filter, sort]);

    // Invoices left out of the summary are listed but not summed, so the total matches the table
    const summedInvoices = countedOnly(visibleInvoices).filter((o) => !unplacedInvoices?.has(o));

    const sortHeader = (key, label, baseStyle) => {
        const active = sort.key === key;
        return (
            <th
                key={key}
                style={{ ...baseStyle, ...sortableStyle }}
                onClick={() => toggleSort(key)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleSort(key); } }}
                tabIndex={0}
                aria-sort={active ? (sort.descending ? 'descending' : 'ascending') : 'none'}
            >
                {label}{active && (sort.descending ? ' ▼' : ' ▲')}
            </th>
        );
    };

    return (
        <div>
            <div style={pageHeaderStyle}>
                <Link onClick={onBack}>&#8592; Tillbaka till sammanställningen</Link>
                <h3 style={{ margin: '0.5rem 0 0' }}>{title}</h3>
                <div style={{ color: '#666', fontSize: '0.85rem' }}>
                    {visibleInvoices.length === invoices.length
                        ? `${invoices.length} fakturor`
                        : `${visibleInvoices.length} av ${invoices.length} fakturor`}
                </div>
                <input
                    type="search"
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    placeholder="Filtrera listan (text och belopp)"
                    aria-label="Filtrera fakturalistan"
                    style={filterInputStyle}
                />
            </div>
            <div style={cardStyle}>
                <table style={tableStyle}>
                    <thead>
                        <tr style={headRowStyle}>
                            <th style={headTextStyle}>Fakturanr</th>
                            <th style={headTextStyle}>Fakturadatum</th>
                            <th style={headTextStyle}>Orderreferens</th>
                            {sortHeader('customer', 'Kund', headTextStyle)}
                            {sortHeader('ourReference', 'Vår referens', headTextStyle)}
                            <th style={headTextStyle}>Webb</th>
                            <th style={headTextStyle}>Bokförd</th>
                            {sortHeader('sent', 'Skickad', headTextStyle)}
                            <th style={headTextStyle}>Spåra</th>
                            {AMOUNT_COLUMNS.map((column) => (column.compare
                                ? sortHeader(column.key, column.label, headStyle)
                                : <th key={column.key} style={headStyle}>{column.label}</th>))}
                        </tr>
                    </thead>
                    <tbody>
                        {visibleInvoices.map((invoice, i) => (
                            <tr key={invoice.documentNumber} style={invoice.cancelled ? { ...stripeStyle(i), textDecoration: 'line-through' } : stripeStyle(i)}>
                                <td style={textCellStyle}>
                                    <Link onClick={() => onSelect(invoice)}>{invoice.documentNumber}</Link>
                                </td>
                                <td style={textCellStyle}>{fmtDate(invoice.invoiceDate)}</td>
                                <td style={textCellStyle}>
                                    {invoice.orderReference > 0
                                        ? <Link onClick={() => onOpenOrder(invoice.orderReference)}>{invoice.orderReference}</Link>
                                        : invoice.orderReference}
                                </td>
                                <td style={textCellStyle}>{invoice.customerName}</td>
                                <td style={textCellStyle}>{invoice.ourReference}</td>
                                <td style={textCellStyle}>{invoice.isWeb ? '✓' : ''}</td>
                                <td style={textCellStyle}>{invoice.booked ? '✓' : ''}</td>
                                <td style={textCellStyle}>{invoice.sent ? '✓' : ''}</td>
                                <td style={textCellStyle}>
                                    {canTrack(invoice) && (
                                        <a href={getTrackingUrl(invoice)} target="_blank" rel="noopener noreferrer" style={linkStyle}>Postnox</a>
                                    )}
                                </td>
                                {AMOUNT_COLUMNS.map((column) => <td key={column.key} style={amountStyle(column.pick(invoice))}>{fmtMoney(column.pick(invoice))}</td>)}
                            </tr>
                        ))}
                    </tbody>
                    <tfoot>
                        <tr style={footRowStyle}>
                            <td style={textCellStyle} colSpan={9}>Totalt ({summedInvoices.length} fakturor)</td>
                            {AMOUNT_COLUMNS.map((column) => {
                                const total = sum(summedInvoices.map((o) => column.pick(o) || 0));
                                return <td key={column.key} style={amountStyle(total)}>{fmtMoney(total)}</td>;
                            })}
                        </tr>
                    </tfoot>
                </table>
            </div>
        </div>
    );
};

export default InvoiceList;
